// TelcoTrack - Object Type Labels (single source of truth)
// České názvy typů objektů – používají backend (object.controller) i frontend (object.utils)

/** Point object: Koncový bod záfuku (no user name; single-token type for PHASE_CODE lookup). */
export const OBJECT_TYPE_KONCOVYBOD = 'KONCOVYBOD' as const;

/**
 * Map object types rendered as point markers (not lines / polygons).
 * Used for admin „Přesunout“ and similar point-only map actions.
 */
export const MAP_POINT_OBJECT_TYPES = [
  'ROZVADEC',
  'BUDOVA',
  'SPOJKOVISTE',
  'JAMA',
  'INFO',
  'KONCOVYBOD',
] as const;

export type MapPointObjectType = (typeof MAP_POINT_OBJECT_TYPES)[number];

export function isMapPointObjectType(type: string | null | undefined): boolean {
  return (MAP_POINT_OBJECT_TYPES as readonly string[]).includes(
    String(type ?? '').trim().toUpperCase(),
  );
}

export const OBJECT_TYPE_NAMES: Record<string, string> = {
  // POINT types
  ROZVADEC: 'Rozvaděč',
  BUDOVA: 'Budova',
  SPOJKOVISTE: 'Spojkoviště',
  JAMA: 'Montážní jáma',
  INFO: 'Info',
  KONCOVYBOD: 'Koncový bod záfuku',
  // LINE types
  VYKOP: 'Výkop',
  /** Legacy DB / import — zobrazit jako Výkop do migrace 065 */
  VYKOP_NOVY: 'Výkop',
  VYKOP_METALIKA: 'Výkop (metalika)',
  STAVAJICI: 'Stávající trasa',
  VYKOP_STAVAJICI: 'Stávající trasa',
  // Special
  UNASSIGNED: 'Nepřiřazeno',
  /** Ruční řádek ve výkazu (bez mapového objektu). */
  MANUAL: 'Ruční záznam',
  // Sklad / audit dokladů (object_history — Historie uživatele, polygon Aktivita)
  WAREHOUSE: 'Sklad',
  WAREHOUSE_GOODS_RECEIPT: 'Příjemka',
  WAREHOUSE_RECEIPT: 'Příjemka',
  WAREHOUSE_ISSUE: 'Výdejka',
  WAREHOUSE_TRANSFER: 'Převodka',
  WAREHOUSE_RETURN: 'Vratka',
  WAREHOUSE_INVENTORY: 'Inventura',
  EQUIPMENT: 'Zařízení',
};

/**
 * Vrací český název typu objektu; při neznámém typu vrátí vstupní kód.
 */
export function getObjectTypeName(type: string): string {
  const raw = String(type ?? '').trim();
  if (!raw) return raw;
  const upper = raw.toUpperCase();
  return OBJECT_TYPE_NAMES[upper] ?? OBJECT_TYPE_NAMES[raw] ?? raw;
}

export function isKoncovyBodZafukuType(type: string | null | undefined): boolean {
  return String(type ?? '').trim().toUpperCase() === OBJECT_TYPE_KONCOVYBOD;
}

/** Lists / Drive leaf — type label + object id (no user-entered name). */
export function formatNamelessObjectLabel(type: string, objectId: string | null | undefined): string {
  const id = (objectId ?? '').trim();
  const label = getObjectTypeName(type);
  return id ? `${label} ${id}` : label;
}

/** Map tooltip — type + phase (Koncový bod záfuku and other nameless points). */
export function formatObjectMapTooltip(
  type: string,
  phaseCode: string | null | undefined,
  phaseLabel?: string | null,
): string {
  const typeName = getObjectTypeName(type);
  const phase = (phaseLabel ?? phaseCode ?? '').trim();
  return phase && phase !== typeName ? `${typeName} — ${phase}` : typeName;
}
